/**
 * The invitee's own clock on the public booking page (3.1).
 *
 * Slots are shown on the MERCHANT's zone by default and the page says so; a
 * booker whose browser runs on a different clock can flip the whole list —
 * days and times — to their own zone, and back. Offered only when the two
 * clocks actually differ right now: an Israeli booker on an Israeli page sees
 * no switch at all.
 */
import { useCallback, useMemo, useState } from 'react';

export function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Minutes the zone is ahead of UTC at `at`. Null for a zone the runtime does not know. */
export function offsetAt(tz: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(at);
    const n = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const local = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'));
    if (Number.isNaN(local)) return null;
    return Math.round((local - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);
  } catch {
    return null;
  }
}

/**
 * A fixed UTC offset as a zone id the runtime can format on, or null.
 *
 * The server sends `timeZone: null` with a `tzOffsetMinutes` when a workspace
 * never chose a zone (see server `effectiveTimeZone`): its slots are on that
 * offset and it is labelled "UTC+03:00". Whole hours map to `Etc/GMT∓N`, which
 * every ICU knows (POSIX sign: `Etc/GMT-3` is UTC+3); other offsets use the
 * ECMA-402 offset id ("+05:30") where the runtime has it. Each candidate is
 * checked against the offset it must produce, so a runtime that misreads one
 * yields null instead of a wrong clock.
 */
export function fixedOffsetZone(offsetMinutes: number): string | null {
  if (!Number.isInteger(offsetMinutes) || Math.abs(offsetMinutes) > 14 * 60) return null;
  if (offsetMinutes === 0) return 'UTC';
  const abs = Math.abs(offsetMinutes);
  const candidates: string[] = [];
  if (abs % 60 === 0) candidates.push(`Etc/GMT${offsetMinutes > 0 ? '-' : '+'}${abs / 60}`);
  candidates.push(`${offsetMinutes > 0 ? '+' : '-'}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`);
  const at = new Date();
  return candidates.find((tz) => offsetAt(tz, at) === offsetMinutes) ?? null;
}

/** The clock the merchant's slots are on: their IANA zone, else the page's fixed offset. Null = unknown. */
export function merchantClock(timeZone: string | null | undefined, offsetMinutes: number | null | undefined): string | null {
  if (timeZone) return timeZone;
  return typeof offsetMinutes === 'number' ? fixedOffsetZone(offsetMinutes) : null;
}

/** Do the two zones read the same wall-clock time at `at`? Unknown zones never match. */
export function sameClock(a: string, b: string, at: Date): boolean {
  if (a === b) return true;
  const oa = offsetAt(a, at);
  const ob = offsetAt(b, at);
  return oa !== null && oa === ob;
}

/** "Eastern Time" where the runtime can name it, else the IANA id made readable. */
export function zoneDisplayName(tz: string, at: Date = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: 'longGeneric' })
      .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value;
    if (name) return name;
  } catch { /* fall through */ }
  return tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
}

export interface ViewZone {
  /** The zone slots are rendered in right now (null = the browser's). */
  shownTz: string | null;
  showingMine: boolean;
  /** The booker's zone, when switching to it would change anything. */
  mine: { tz: string; label: string } | null;
  /** Flips the view and returns the zone now shown. */
  toggle: () => string | null;
}

export function useViewZone(merchantTz: string | null, mineTz: string | null = browserZone(), now?: Date): ViewZone {
  const [showingMine, setShowingMine] = useState(false);
  // Fixed at mount: whether the clocks differ is a property of the visit.
  const [nowMs] = useState(() => (now ?? new Date()).getTime());
  const mine = useMemo(() => {
    if (!merchantTz || !mineTz || sameClock(merchantTz, mineTz, new Date(nowMs))) return null;
    return { tz: mineTz, label: zoneDisplayName(mineTz, new Date(nowMs)) };
  }, [merchantTz, mineTz, nowMs]);
  const active = showingMine && mine !== null;
  const shownTz = active ? mine.tz : merchantTz;
  const toggle = useCallback((): string | null => {
    const next = !active;
    setShowingMine(next);
    return next && mine ? mine.tz : merchantTz;
  }, [active, mine, merchantTz]);
  return { shownTz, showingMine: active, mine, toggle };
}
